import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, MapPin, FileText, Star, Wrench, DoorOpen, DoorClosed,
  LayoutGrid, Grip, Lock, Wind, Settings, Search, Square, HelpCircle, CheckCircle2,
} from "lucide-react";
import { PublicLayout } from "../components/Layout";
import { useI18n } from "../lib/i18n";
import { api, euro } from "../lib/api";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const ICONS = {
  "layout-grid": LayoutGrid, "door-open": DoorOpen, "door-closed": DoorClosed, square: Square,
  grip: Grip, lock: Lock, wind: Wind, settings: Settings, search: Search, wrench: Wrench,
};

const HERO_IMG = "https://images.unsplash.com/photo-1783587616528-a37972228563?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHx3aW5kb3clMjBkb29yJTIwcmVwYWlyJTIwdGVjaG5pY2lhbiUyMGNyYWZ0c21hbnNoaXB8ZW58MHx8fHwxNzg4NTM2NDA5fDA&ixlib=rb-4.1.0&q=85";

function Stars({ value }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={16} className={i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
      ))}
    </div>
  );
}

export default function Home() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [faq, setFaq] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average: null, count: 0 });

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/faq").then((r) => setFaq(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const goBook = (serviceId) => navigate(serviceId ? `/boeken?service=${serviceId}` : "/boeken");

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute inset-0 hero-grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-orange-300 border border-white/10">
              <MapPin size={13} /> {t("hero.badge1")}
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">{t("hero.subtitle")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button className="cta-orange rounded-full px-7 py-6 text-base" onClick={() => goBook()} data-testid="hero-book-btn">
                {t("hero.cta")} <ArrowRight size={18} className="ml-1" />
              </Button>
              <Button
                variant="outline"
                className="rounded-full px-7 py-6 text-base bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
                onClick={() => goBook()}
                data-testid="hero-dontknow-btn"
              >
                <HelpCircle size={18} className="mr-1" /> {t("hero.cta2")}
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><ShieldCheck size={16} className="text-orange-400" /> {t("hero.badge2")}</span>
              <span className="flex items-center gap-1.5"><FileText size={16} className="text-orange-400" /> {t("hero.badge3")}</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <img src={HERO_IMG} alt="Vakman herstelt raam" className="rounded-2xl shadow-2xl object-cover w-full h-[320px] lg:h-[440px]" />
            <div className="absolute -bottom-5 -left-3 sm:left-5 bg-white text-slate-900 rounded-xl shadow-xl p-4 flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-700"><CheckCircle2 /></span>
              <div>
                <p className="text-sm font-bold leading-tight">{t("value.prop")}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* POPULAR REPAIRS */}
      <section id="herstellingen" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{t("popular.title")}</h2>
          <p className="mt-3 text-slate-600">{t("popular.subtitle")}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <button
                key={s.id}
                onClick={() => goBook(s.id)}
                data-testid={`service-card-${s.id}`}
                className="group text-left bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-orange-300 transition-all"
              >
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#0F172A] text-white group-hover:bg-brand-orange transition-colors">
                  <Icon size={20} />
                </span>
                <h3 className="mt-4 font-heading font-semibold text-slate-900">{lang === "fr" ? s.name_fr : s.name_nl}</h3>
                <p className="mt-1 text-sm text-slate-500 line-clamp-2">{lang === "fr" ? s.description_fr : s.description_nl}</p>
                <div className="mt-4 flex items-center justify-end">
                  <span className="text-brand-orange text-sm font-semibold flex items-center gap-1">{t("popular.book")} <ArrowRight size={14} /></span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="hoe-werkt-het" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 text-center">{t("how.title")}</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className="relative bg-slate-50 rounded-xl p-6 border border-slate-100">
                <span className="absolute -top-3 -left-3 h-9 w-9 rounded-lg bg-brand-orange text-white font-heading font-bold flex items-center justify-center shadow">{n}</span>
                <p className="mt-2 font-medium text-slate-800">{t(`how.${n}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="rounded-2xl bg-[#1E293B] text-white p-10 lg:p-16 text-center">
          <ShieldCheck className="mx-auto text-brand-orange" size={40} />
          <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold">{t("trust.title")}</h2>
          <p className="mt-3 text-slate-300 max-w-2xl mx-auto">{t("trust.subtitle")}</p>
          <Button className="cta-orange rounded-full px-7 py-6 mt-8" onClick={() => goBook()} data-testid="trust-book-btn">
            {t("hero.cta")} <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.count > 0 && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-center gap-4">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("reviews.title")}</h2>
              <div className="flex items-center gap-2">
                <Stars value={reviews.average} />
                <span className="text-sm text-slate-500">{reviews.average} · {reviews.count} {t("reviews.count")}</span>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.reviews.slice(0, 6).map((r) => (
                <div key={r.id} className="bg-slate-50 rounded-xl p-6 border border-slate-100" data-testid={`review-${r.id}`}>
                  <Stars value={r.rating} />
                  <p className="mt-3 text-slate-700 text-sm">"{r.comment}"</p>
                  <p className="mt-3 text-xs font-semibold text-slate-500">{r.customer_name} — {r.city}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 text-center">{t("faq.title")}</h2>
        <Accordion type="single" collapsible className="mt-8">
          {faq.slice(0, 8).map((f) => (
            <AccordionItem key={f.id} value={f.id} data-testid={`faq-item-${f.id}`}>
              <AccordionTrigger className="text-left font-medium">{lang === "fr" ? f.question_fr : f.question_nl}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{lang === "fr" ? f.answer_fr : f.answer_nl}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-8 text-center">
          <Link to="/faq" className="text-brand-orange font-semibold text-sm hover:underline">{t("faq.title")} →</Link>
        </div>
      </section>
    </PublicLayout>
  );
}
